import { useState } from 'react';
import { ProductDetailsStore } from '../../../lib/state/hessel-site/product-details';
import { hesselViewModels } from '../../../lib/view-models';

type Props = {
    insuranceProducts: hesselViewModels.Equipment[];
};

type SelectedInsuranceHandling = {
    updateInsuranceProduct: (insuranceProduct: hesselViewModels.Equipment, selected: boolean) => void;
    isMoreExpensiveInsuranceSelected: (insuranceProduct: hesselViewModels.Equipment) => boolean;
};

export const useSelectedInsuranceHandling = ({ insuranceProducts }: Props): SelectedInsuranceHandling => {
    const { updateOptionalEquipment } = ProductDetailsStore.useStoreActions((actions) => actions);
    const [userSelectedInsuranceProducts, setUserSelectedInsuranceProducts] = useState<hesselViewModels.Equipment[]>([]);

    const updateInsuranceProduct = (insuranceProduct: hesselViewModels.Equipment, selected: boolean) => {
        if (selected) {
            const updatedUserSelectedInsuranceProducts = new Set([...userSelectedInsuranceProducts, insuranceProduct]);
            setUserSelectedInsuranceProducts(Array.from(updatedUserSelectedInsuranceProducts));
            const selectedInsuranceProducts = insuranceProducts.filter((product) => product.isSelected && product.id !== insuranceProduct.id);
            const lessExpensiveSelectedInsuranceProducts = selectedInsuranceProducts.filter(
                (product) => (product.monthlyHireSelectedPrice ?? 0) < (insuranceProduct.monthlyHireSelectedPrice ?? 0)
            );
            for (const product of lessExpensiveSelectedInsuranceProducts) {
                updateOptionalEquipment({ id: product.id, isSelected: false });
            }
            updateOptionalEquipment({ id: insuranceProduct.id, isSelected: selected });
        } else {
            const updatedUserSelectedInsuranceProducts = userSelectedInsuranceProducts.filter((product) => product.id !== insuranceProduct.id);
            if (updatedUserSelectedInsuranceProducts.length > 0) {
                const mostExpensiveSelectedInsuranceProduct = updatedUserSelectedInsuranceProducts.sort(
                    (a, b) => (b.monthlyHireSelectedPrice ?? 0) - (a.monthlyHireSelectedPrice ?? 0)
                )[0];
                updateOptionalEquipment({ id: mostExpensiveSelectedInsuranceProduct.id, isSelected: true });
            }
            setUserSelectedInsuranceProducts(updatedUserSelectedInsuranceProducts);
            updateOptionalEquipment({ id: insuranceProduct.id, isSelected: selected });
        }
    };

    const isMoreExpensiveInsuranceSelected = (insuranceProduct: hesselViewModels.Equipment): boolean => {
        const selectedInsuranceProducts = insuranceProducts.filter((product) => product.isSelected && product.id !== insuranceProduct.id);
        const moreExpensiveSelectedInsuranceProducts = selectedInsuranceProducts.filter(
            (product) => (product.monthlyHireSelectedPrice ?? 0) > (insuranceProduct.monthlyHireSelectedPrice ?? 0)
        );
        return moreExpensiveSelectedInsuranceProducts.length > 0;
    };

    return {
        updateInsuranceProduct,
        isMoreExpensiveInsuranceSelected,
    };
};
